import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from "../../../../messages/en-US";
import moment from "moment";
import { SortDescriptor, process, State } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn as Column, GridToolbar} from "@progress/kendo-react-grid";
import { CustomTextFilterCell } from "../../../Shared/Components/Table/CustomTextFilterCell";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { downloadIcon } from "@progress/kendo-svg-icons";

interface Props {
  events: any;
  aggregator: string;
  endpointSerialNo?: string;
  isSimplified?: boolean; // For Locomotives - only show flow rate and volume
}

interface Page {
  skip: number;
  take: number;
}

const page: Page = { skip: 0, take: 30 };

export default function EndpointFlowRateEventsTable(props: Props): ReactElement {
  const localizationService = useLocalization();
  const aggregator = props.aggregator;
  const isSimplified = props.isSimplified === true;
  const initialSort: Array<SortDescriptor> = [{ field: "timestamp", dir: "desc" }];
  const [sort] = React.useState(initialSort);
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  //Creating dataState to prevent issues with virtual scrolling
  const createAppState = (dataState: State) => {
    return {
      result: process(props.events, dataState),
      dataState: dataState,
    };
  };

  const [dataState, setDataState] = useState<any>(
    createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    })
  );
  
  const dataStateChange = (event) => {
    setDataState(createAppState(event.dataState));
  };

   useEffect(() => {
    setDataState( createAppState({
      skip: page.skip,
      take: page.take,
      sort: sort
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  //Formating Cells
  const OccurredCell = (props: any) => {
    return (
      <td>
         {aggregator === 'none' ? 
          <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY @ HH:mm:ss')}</div>
          :
          <div>{moment(props.dataItem.timestamp).format('DD/MM/YYYY')}</div>
        }
      </td>
      );
  }

  const FlowRateCell = (props: any) => {
    return (
      <td>
        <div>{(props.dataItem.flowRate || props.dataItem.avgFlowRate || 0).toFixed(2)}</div>
      </td>
      );
  }

  const VolumeCell = (props: any) => {
    return (
      <td>
        <div>{(props.dataItem.volume || 0).toFixed(2)}</div>
      </td>
      );
  }

  const exportData = useMemo(() => {
    return props.events.map((item) => ({
      ...item,
      timestampExport: props.aggregator === "none" ? moment(item.timestamp).format('DD/MM/YYYY HH:mm:ss') : moment(item.timestamp).format('DD/MM/YYYY'),
      
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.events]);

  const getExcelExportData = () => {
    return process(exportData, {filter: dataState.dataState.filter, sort: dataState.dataState.sort}).data;
  };
  
  return (
    <div>
      <ExcelExport data={getExcelExportData()} ref={_export} collapsible={true} fileName={`Flow Rate Events - ${props.endpointSerialNo !== undefined ? props.endpointSerialNo : props.events[0]?.endpointName}.xlsx`}>
        <ExcelExportColumn field="timestampExport" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} width={150}/>
        {!isSimplified && <ExcelExportColumn field="serialNo" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`} />}
        {!isSimplified && <ExcelExportColumn field="minFlowRate" title={`${localizationService.toLanguageString('custom.minFlowRate', enMessages.custom.minFlowRate)}`} cellOptions={{ format: "0.00" }} />}
        <ExcelExportColumn field={isSimplified ? "flowRate" : "avgFlowRate"} title={`${localizationService.toLanguageString('custom.flowRate', enMessages.custom.flowRate)}`} cellOptions={{ format: "0.00" }}/>   
        {!isSimplified && <ExcelExportColumn field="maxFlowRate" title={`${localizationService.toLanguageString('custom.maxFlowRate', enMessages.custom.maxFlowRate)}`} cellOptions={{ format: "0.00" }}/>}
        {!isSimplified && <ExcelExportColumn field="windowSize" title={`${localizationService.toLanguageString('custom.windowSize', enMessages.custom.windowSize)}`}/>}
        {!isSimplified && <ExcelExportColumn field="samplePeriod" title={`${localizationService.toLanguageString('custom.samplePeriod', enMessages.custom.samplePeriod)}`}/>}
        <ExcelExportColumn field="volume" title={`${localizationService.toLanguageString('custom.volumeInL', enMessages.custom.volumeInL)}`} cellOptions={{ format: "0.00" }}/>
      </ExcelExport>
      <Grid
        key={props.events.length.toString()}
        rowHeight={46}
        take={page.take}
        data={dataState.result}
        scrollable={"virtual"}
        filterable={true}
        onDataStateChange={dataStateChange}
        {...dataState.dataState}
        sortable={true}
        style={{
          height: `calc(100vh - 300px)`,
        }}
        className=" w-full z-0 extra-filter-button "
      >
        <GridToolbar>  
        <div style={{width:'100%', display:'flex', justifyContent:'flex-end'}}>
              <Tooltip anchorElement="target" position="bottom">
                <Button
                  title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                  svgIcon={downloadIcon}
                  disabled={dataState.result.data.length === 0}
                  onClick={excelExport}
                />
              </Tooltip>
          </div>
        </GridToolbar>
        <Column field="timestamp" title={`${localizationService.toLanguageString('custom.occurred', enMessages.custom.occurred)}`} cell={OccurredCell} filterable={false}/>
        {!isSimplified && <Column field="serialNo" title={`${localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}`}  filterCell={CustomTextFilterCell}/>}
        {!isSimplified && <Column field="minFlowRate" title={`${localizationService.toLanguageString('custom.minFlowRate', enMessages.custom.minFlowRate)}`} filterCell={CustomTextFilterCell}/>}
        <Column field={isSimplified ? "flowRate" : "avgFlowRate"} title={`${localizationService.toLanguageString('custom.flowRate', enMessages.custom.flowRate)}`} filterCell={CustomTextFilterCell} cell={FlowRateCell} />   
        {!isSimplified && <Column field="maxFlowRate" title={`${localizationService.toLanguageString('custom.maxFlowRate', enMessages.custom.maxFlowRate)}`} filterCell={CustomTextFilterCell}/>}
        {!isSimplified && <Column field="windowSize" title={`${localizationService.toLanguageString('custom.windowSize', enMessages.custom.windowSize)}`} filterCell={CustomTextFilterCell}/>}
        {!isSimplified && <Column field="samplePeriod" title={`${localizationService.toLanguageString('custom.samplePeriod', enMessages.custom.samplePeriod)}`} filterCell={CustomTextFilterCell}/>}
        <Column field="volume" title={`${localizationService.toLanguageString('custom.volumeInL', enMessages.custom.volumeInL)}`} filterCell={CustomTextFilterCell} cell={VolumeCell}/>
      </Grid>     
  </div>
  );
}